import React, { useState, useRef, useEffect } from 'react';

export function OmniSearch({ searchQuery, setSearchQuery, columns, fkOptions }) {
    const [showDropdown, setShowDropdown] = useState(false);
    const [cursorIdx, setCursorIdx] = useState(0);
    const [suggestions, setSuggestions] = useState([]);
    
    const inputRef = useRef(null);

    // Logic to parse the current input to decide what suggestions to show
    useEffect(() => {
        if (!searchQuery.includes('@')) {
            setShowDropdown(false);
            return;
        }

        const match = searchQuery.match(/@(\w*)$/); // Match column name typing
        const colMatch = searchQuery.match(/@(\w+):\s*(.*)$/); // Match value typing after @ColumnName:

        if (colMatch) {
            const colName = colMatch[1];
            const valSearch = colMatch[2].toLowerCase();
            
            // Check if column exists and is a foreign key
            if (fkOptions[colName]) {
                const fkOpts = fkOptions[colName].options;
                const filtered = fkOpts.filter(o => 
                    String(o.label).toLowerCase().includes(valSearch)
                );
                
                setSuggestions(filtered.map(f => ({
                    type: 'value',
                    colName: colName,
                    text: f.label,
                    insertText: `@${colName}: "${f.label}" `
                })));
                setShowDropdown(filtered.length > 0);
            } else {
                setShowDropdown(false);
            }
        } else if (match) {
            const typedCol = match[1].toLowerCase();
            const filteredCols = columns.filter(c => c.ColumnName.toLowerCase().includes(typedCol));
            
            setSuggestions(filteredCols.map(c => ({
                type: 'column',
                text: c.ColumnName,
                insertText: `@${c.ColumnName}: `
            })));
            setShowDropdown(filteredCols.length > 0);
        } else {
            setShowDropdown(false);
        }
        setCursorIdx(0);
    }, [searchQuery, columns, fkOptions]);

    const handleKeyDown = (e) => {
        if (!showDropdown) return;
        
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setCursorIdx(prev => (prev + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setCursorIdx(prev => (prev - 1 + suggestions.length) % suggestions.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (suggestions[cursorIdx]) {
                applySuggestion(suggestions[cursorIdx]);
            }
        } else if (e.key === 'Escape') {
            setShowDropdown(false);
        }
    };

    const applySuggestion = (suggestion) => {
        let newQuery = searchQuery;
        
        if (suggestion.type === 'column') {
            newQuery = searchQuery.replace(/@\w*$/, suggestion.insertText);
        } else if (suggestion.type === 'value') {
            newQuery = searchQuery.replace(/@\w+:\s*[^"]*$/, suggestion.insertText);
        }
        
        setSearchQuery(newQuery);
        setShowDropdown(false);
        inputRef.current?.focus();
    };

    return (
        <div className="relative w-full max-w-xl">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
                <span className="material-symbols-outlined text-lg">search</span>
            </div>
            <input
                ref={inputRef}
                type="text"
                className="bg-slate-900 border border-slate-700 text-slate-200 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5 transition-colors"
                placeholder="Search table... Use @ColumnName: value to filter specific fields"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
            />
            {searchQuery && (
                <button 
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-200"
                    onClick={() => setSearchQuery('')}
                >
                    <span className="material-symbols-outlined text-sm">close</span>
                </button>
            )}

            {showDropdown && suggestions.length > 0 && (
                <ul className="absolute z-50 w-full mt-1 bg-slate-800 border border-slate-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {suggestions.map((s, idx) => (
                        <li 
                            key={idx}
                            onClick={() => applySuggestion(s)}
                            className={`px-4 py-2 cursor-pointer text-sm ${idx === cursorIdx ? 'bg-blue-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
                        >
                            {s.type === 'column' ? (
                                <span><span className="text-blue-400">@</span>{s.text}</span>
                            ) : (
                                <span>{s.text} <span className="text-slate-500 text-xs ml-2">in {s.colName}</span></span>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
